// What to tell a person when something fails. Branch on the problem `code` only.
import { isApiError, isNetworkError } from '@qrkey/api-client';
import { OfflineRefusal } from '../offline/refusal.js';

function minutes(seconds: number | undefined): string {
  if (seconds === undefined) return 'a few minutes';
  const m = Math.max(1, Math.ceil(seconds / 60));
  return m === 1 ? '1 minute' : `${m} minutes`;
}

export function errorMessage(
  error: unknown,
  context: 'password' | 'otp' | 'pin' | 'other' = 'other',
): string {
  if (error instanceof OfflineRefusal) return error.message;
  if (isNetworkError(error)) return 'Cannot reach QRkey. Check the connection and try again.';
  if (!isApiError(error)) return 'Something went wrong. Try again.';
  switch (error.code) {
    case 'INVALID_CREDENTIALS':
      if (context === 'pin') return 'Wrong PIN. Try again.';
      if (context === 'otp') return 'That code is not right or has expired.';
      return 'Those sign-in details are not right. Check them and try again.';
    case 'ACCOUNT_LOCKED':
      return `Too many wrong tries. Try again in ${minutes(error.retryAfterSeconds)}.`;
    case 'RATE_LIMITED':
      return `Too many attempts. Wait ${error.retryAfterSeconds ?? 30} seconds and try again.`;
    case 'TERMINAL_BLOCKED':
      return 'This device has been blocked. Ask a manager to check it in Terminals.';
    case 'TERMINAL_NOT_REGISTERED':
      return 'This device is not registered at the outlet any more. Set it up again.';
    case 'SERVICE_UNAVAILABLE':
      return context === 'otp'
        ? 'Sign-in codes cannot be sent right now. Use your password.'
        : 'QRkey is not available right now. Try again shortly.';
    case 'TABLE_OCCUPIED':
      return 'That table already has an open order. Open it from the table map.';
    case 'VERSION_CONFLICT':
      return 'Someone else changed this just now. It has been refreshed; try again.';
    case 'ORDER_BILLED':
      return 'The order is billed. Reopen it to change it.';
    case 'ORDER_NOT_FULLY_PAID':
      return 'The order is not fully paid yet.';
    case 'PRINTER_NOT_CONFIGURED':
      return 'No printer is set up for this. A manager adds one in Printers.';
    case 'INSUFFICIENT_CASH':
      return 'The drawer does not hold that much cash.';
    case 'SHIFT_ALREADY_OPEN':
      return 'A shift is already open here. Close it first.';
    case 'SHIFT_NOT_OPEN':
      return 'That shift is already closed.';
    case 'FORBIDDEN':
      return 'Your role cannot do this.';
    default:
      return error.message;
  }
}
